import {
  Button,
  EmptyState,
  ErrorMessage,
  GreyGrey80,
  GreyGrey90,
  IconSquareGridCircle,
  IndigoIndigo60,
  Link,
  Primary12Regular,
  Primary13SemiBold,
  RadioGroup,
  Tooltip,
  Modal,
  Box,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import { MODAL_CONTENT_HEIGHT_PX } from '../constants'
import { useAtom, useSetAtom } from 'jotai'
import { integrationStepSkippedAtom, selectedIntegrationAtom } from '../atoms'
import { IntegrationServices } from '@sequencehq/api'
import { getLogo } from 'lib/integrations/logos'
import { match } from 'ts-pattern'
import { integrationName } from 'lib/integrations/integrationName'
import { IntegrationsBrowserModal } from 'Integrations/index'
import { useConnectedIntegrationCustomers } from 'Customers/utils/useConnectedIntegrationCustomers'
import { IntegrationMoreInfo } from 'lib/docsLink/integrations'
import { HubspotImportStagesInfo } from '../utils/HubspotImportStagesInfo'
import { useIntegrationSelectionDefaults } from '../hooks/useIntegrationSelectionDefaults'

export const IntegrationSelection = (props: {
  onContinue: () => void
  onCancel: () => void
}) => {
  const [selectedIntegration, setSelectedIntegration] = useAtom(
    selectedIntegrationAtom
  )
  const setIntegrationStepSkipped = useSetAtom(integrationStepSkippedAtom)
  const { isLoading, isError, integrations } =
    useConnectedIntegrationCustomers()
  const isSubmitDisabled =
    isLoading || Boolean(integrations?.length && !selectedIntegration)

  useIntegrationSelectionDefaults({
    integrations,
    isReady: !isLoading && !isError,
    selectedIntegration,
    onSelect: setSelectedIntegration,
    onSkip: () => {
      setIntegrationStepSkipped(true)
      props.onContinue()
    }
  })

  return (
    <>
      <Modal.Content
        minHeight={`${MODAL_CONTENT_HEIGHT_PX}px`}
        maxHeight={`${MODAL_CONTENT_HEIGHT_PX}px`}
      >
        {match({
          isLoading,
          isError,
          integrations
        })
          .with({ isLoading: true }, () => (
            <Modal.LoadingSpinner
              height={`${MODAL_CONTENT_HEIGHT_PX - 40}px`}
            />
          ))
          .with({ isError: true }, () => (
            <Flex
              height={`${MODAL_CONTENT_HEIGHT_PX - 40}px`}
              width="100%"
              alignItems="center"
              justifyContent="center"
            >
              <ErrorMessage message="Could not load existing connectors" />
            </Flex>
          ))
          .with({ integrations: [] }, () => (
            <EmptyState
              icon={() => <IconSquareGridCircle size={24} color={GreyGrey90} />}
              title="No connectors to show"
              body={
                <Flex direction="column" gap="16px">
                  <Box>Add a connector to import customers.</Box>
                  <IntegrationsBrowserModal
                    trigger={
                      <Button variant="secondary">Browse connectors</Button>
                    }
                  />
                </Flex>
              }
            />
          ))
          .otherwise(() => (
            <Grid gridTemplateRows="auto auto auto" gap="8px">
              <Box {...Primary13SemiBold} color={GreyGrey80} marginBottom="4px">
                Choose an integration to import from
              </Box>
              <RadioGroup
                name="selectedIntegration"
                onChange={newValue =>
                  setSelectedIntegration(newValue as IntegrationServices)
                }
                value={selectedIntegration ?? ''}
                options={
                  integrations?.map(integration => ({
                    label: (
                      <Flex gap="6px" alignItems="center">
                        {getLogo(integration.service, 16)}
                        {integrationName(integration.service)}
                        {integration.service === 'HubSpot' && (
                          <HubspotImportStagesInfo />
                        )}
                      </Flex>
                    ),
                    value: integration.service,
                    description: `${integration.totalLinkedCustomers} of ${integration.totalCustomers} customers linked`
                  })) ?? []
                }
              />
              <GridItem>
                <Link
                  {...Primary12Regular}
                  color={IndigoIndigo60}
                  to={IntegrationMoreInfo.OverviewLink}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Learn more
                </Link>
              </GridItem>
            </Grid>
          ))}
      </Modal.Content>
      <Modal.Footer>
        <Tooltip
          label="Please select a connector to import customers from."
          isDisabled={!isSubmitDisabled}
        >
          <Button
            disabled={isSubmitDisabled}
            onClick={() => {
              if (integrations?.length) {
                props.onContinue()
                return
              }
              props.onCancel()
            }}
            data-testid="importCustomers.integrationSelection.continue"
          >
            {isLoading || integrations?.length ? 'Continue' : 'Done'}
          </Button>
        </Tooltip>
      </Modal.Footer>
    </>
  )
}
